<template>
    <slot />
</template>

<script setup>
    import { useEnvMapLoader, useThreeAnimations } from '#imports';
    import { useEventBus } from '@vueuse/core';
    import { gsap } from 'gsap';
    import { LinearSRGBColorSpace, MeshStandardMaterial, PointLight, Vector3 } from 'three';
    import { computed, inject, onMounted, reactive, ref, shallowRef, watch } from 'vue';

    import { LoaderEvent } from '@resn/gozer-loading';
    import { lerp, toRadians } from '@resn/gozer-math';
    import { basePaneOptions, usePane, useRafBool, useThreeObject } from '@resn/gozer-vue';

    import BoxMaterial from './materials/BoxMaterial';
    import { getAllMeshes } from './utils';
    import { useLoaderComplete } from '~/composables/useLoaderComplete';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { createLogger } from '~/utils/logger';

    const logger = createLogger('gl.box');

    const props = defineProps({
        active: { type: Boolean, default: true },
        image: { default: '/textures/box/map-rgb-box@lg.webp' },
        color: { default: '#131313' },

        hasPane: { type: Boolean, default: true },
        hasAmbientMotion: { type: Boolean, default: true },
        hasCompressedTextures: { type: Boolean, default: true },
    });

    const propsReactive = reactive({
        mcRotate: 2,
        mcRotateWorld: 0,

        color: props.color,

        roughnessOut: 0.96,
        metalnessOut: 0,
        reflectivityOut: 0.45,
        bumpScaleOut: 2,
        lightMapIntensity: 1,

        motionAmb: 1,
        speed: 2,

        envMapX: 3.28,
        envMapY: 1.14,
        envMapZ: -0.25,
        // envMapX: -1.36,
        // envMapY: -0.11,
        // envMapZ: -0.72,
        envMapIntensity: 3,
        // envMapIntensity: 5,
    });

    const tweens = reactive({
        introProgress: 0,
        openProgress: 0,
        downProgress: 0,
        backProgress: 0,
    });

    const active = computed(() => props.active && loaded.value);
    const loaded = ref(false);
    const visible = ref(true);

    const animations = shallowRef([]);
    const boxMeshes = shallowRef([]);

    const { setupAnimations, updateAnimations, getAnimations } = useThreeAnimations();

    const { object } = useThreeObject(null, { name: 'Box Container', nesting: false });

    // Needed for pack to append to
    const { object: packContainer } = useThreeObject(null, {
        name: 'Pack Container',
        addToParent: false,
        nesting: true,
    });

    const boxOuter = shallowRef(null);
    const boxInner = shallowRef(null);

    const bus = useEventBus('box');
    const globalAssets = useGlobalAssets();

    const { renderer } = inject('renderer');
    const { loadEnvMap } = useEnvMapLoader();

    const baseMaterial = new MeshStandardMaterial({ color: '#333' });

    const vPosIntro = new Vector3();
    const vPosAmbient = new Vector3();

    const vRotIntro = new Vector3(-1.36, -0.11, -0.72);
    const vRotAmbient = new Vector3();

    let tlAnim;
    let hideDelay;

    const shaderOut = new BoxMaterial({
        reflectivity: 0.5,
    });

    const shaderFoam = new BoxMaterial(
        {
            color: '#0C0D0F',
            roughness: 0.9,
            metalness: 0,
            bumpScale: 2.2,
            reflectivity: 0,
        },
        { isFoam: true }
    );
    const shaders = [shaderFoam, shaderOut];

    const onLoadComplete = ({ data }) => {
        const { gltf } = data;
        const boxMain = gltf.scene.getObjectByName('box');

        for (const key in data) {
            const map = data[key];
            map.name = key;
            map.colorSpace = LinearSRGBColorSpace;
            map.flipY = true;
            map.needsUpdate = true;
        }

        updateTextures(data);

        setupAnimations({ animations: gltf.animations, object: gltf.scene });

        const outer = boxMain.getObjectByName('box-mesh');
        boxOuter.value = outer;
        boxInner.value = boxMain.getObjectByName('Box');

        boxOuter.value.layers.enable(1);
        boxInner.value.layers.enable(1);

        animations.value = getAnimations();
        boxMeshes.value = getAllMeshes(outer);

        closeBoxDoors();
        disposeMaterial(outer);

        outer.add(packContainer);
        object.add(boxMain);

        setVisible();

        setupIntroAnimation();
        updateTweens();

        if (globalAssets.assets) updateMaterial();
        else globalAssets.loader.once(LoaderEvent.LOAD_COMPLETE, updateMaterial);

        // Load environment map
        loadEnvMap(renderer, '/textures/env-map/env-map-global.exr').then(({ map }) => {
            shaders.forEach((shader) => (shader.envMap = map.value));
            baseMaterial.envMap = map.value;
        });

        loaded.value = true;

        emit('loaded');
    };

    const disposeMaterial = () => {
        boxMeshes.value.forEach((node) => {
            node.material.visible = false;
            node.material.dispose();
        });
    };

    const updateMaterial = () => {
        boxMeshes.value.forEach((node) => {
            node.renderOrder = node.userData['renderorder'];
            // node.material = baseMaterial;
            node.material = node.name.includes('foam') ? shaderFoam : shaderOut;
        });
        updateTextures({});
    };

    let readyCount = 0;
    let readyTriggered = false;
    const checkReady = () => {
        if (readyCount === 3 && !readyTriggered) {
            bus.emit('ready'); // Emit "ready" event once both textures and RGB are loaded
            bus.reset();
            readyTriggered = true;
        }
    };

    const updateTextures = ({
        mapRGB,
        mapBumpOut,
        mapMatcapMatte,
        mapNoiseIn,
        mapAOOut,
        mapLightOut,
        mapAOIn,
        mapLightIn,
    }) => {
        // ― OUT
        if (mapMatcapMatte) shaderOut.tMatcap0 = mapMatcapMatte;
        if (mapRGB) shaderOut.tRGB = mapRGB;
        if (mapAOOut) shaderOut.aoMap = mapAOOut;
        if (mapLightOut) shaderOut.lightMap = mapLightOut;
        if (mapBumpOut) shaderOut.bumpMap = mapBumpOut;
        if (mapBumpOut) shaderOut.roughnessMap = mapBumpOut;

        // ― IN
        if (mapNoiseIn) {
            shaderFoam.tNoise = mapNoiseIn;
            shaderFoam.roughnessMap = mapNoiseIn;
            shaderFoam.metallnessMap = mapNoiseIn;
            shaderFoam.bumpMap = mapNoiseIn;
        }
        if (mapLightIn) shaderFoam.lightMap = mapLightIn;
        if (mapAOIn) shaderFoam.aoMap = mapAOIn;

        const { assets } = globalAssets;

        if (assets) {
            shaderOut.tMatcapIrri = assets.matcapRevised6;
        }

        shaders.forEach((shader) => {
            shader.defines.USE_MATCAP_BASE = shader.tMatcap !== null;
            shader.defines.USE_MATCAP_0 = shader.tMatcap0 !== null;
            shader.defines.USE_MATCAP_IRRI = shader.tMatcapIrri !== null;
            shader.defines.USE_NOISE = shader.tNoise !== null;
        });

        readyCount++;
        checkReady();
    };

    const updateTweens = () => {
        const inner = boxOuter.value;
        const mesh = boxInner.value;

        if (!inner || !mesh) return;

        let zObject = lerp(tweens.introProgress, -4, 0);
        let ryObject = lerp(tweens.introProgress, -180, -40);
        let rxInner = lerp(tweens.introProgress, -90, -90);

        zObject = lerp(tweens.openProgress, zObject, 0);
        ryObject = lerp(tweens.openProgress, ryObject, 0);
        rxInner = lerp(tweens.openProgress, rxInner, 0);

        let zMesh = lerp(tweens.backProgress, 0, -1);
        let yMesh = lerp(tweens.downProgress, 0, -4);
        const offset = 90;
        let rxMesh = lerp(tweens.openProgress, 0 + offset, -40 + offset);

        object.position.set(0, 0, zObject);
        object.rotation.set(0, toRadians(ryObject), 0);
        inner.rotation.set(toRadians(rxInner), 0, 0);

        mesh.position.set(0, yMesh, zMesh);
        mesh.rotation.set(toRadians(rxMesh), 0, 0);
    };

    useRafBool(active, ({ timestamp, delta }) => {
        const ts = timestamp / 1000;
        const { speed, motionAmb } = propsReactive;

        // Update the animation mixer
        updateAnimations({ delta, timestamp });

        vPosAmbient.set(0, Math.sin(ts * speed * 0.14), 0).multiplyScalar(0.2 * motionAmb);
        vRotAmbient
            .set(0, Math.sin(ts * speed * 0.3) * 0.2, Math.cos(ts * speed * -0.1) * 0.3)
            .multiplyScalar(0.4 * motionAmb);
    });

    const setUniforms = (props_) => {
        // ― OUT
        shaderOut.roughness = props_.roughnessOut;
        shaderOut.metalness = props_.metalnessOut;
        shaderOut.reflectivity = props_.reflectivityOut;
        shaderOut.bumpScale = props_.bumpScaleOut;
        shaderOut.envMapIntensity = props_.envMapIntensity;
        shaderOut.lightMapIntensity = props_.lightMapIntensity;
        shaderOut.color.set(props_.color);
        shaderOut.fogColor = '#000000';
        shaderOut.envMapRotation.set(props_.envMapX, props_.envMapY, props_.envMapZ);

        // ― IN
        shaderFoam.envMapIntensity = 0.6;
        shaderFoam.aoMapIntensity = 1;
        shaderFoam.lightMapIntensity = props_.lightMapIntensity;

        shaders.forEach((shader) => {
            shader.u_matcapRotation = props_.mcRotate;
            shader.u_matcapRotationWorld = props_.mcRotateWorld;

            shader.uniforms.fogNear.value = -1;
            shader.uniforms.fogFar.value = -3;
        });

        baseMaterial.envMapRotation.set(props_.envMapX, props_.envMapY, props_.envMapZ);
        baseMaterial.envMapIntensity = props_.envMapIntensity;
    };

    const { hasCompressedTextures } = props;
    useLoaderComplete(
        {
            gltf: '/glb/box-etc1s@lg.glb',
            ...{
                mapMatcapMatte: '/textures/box/map-box-matcap-rough-updated@lg.webp#texture',
                mapBumpOut: '/textures/box/map-box-noise.png#texture',
                mapAOOut: '/textures/box/map-box-out-ao@lg.webp#texture',
                mapLightOut: '/textures/box/map-box-out-shadow@lg.webp#texture',

                mapNoiseIn: '/textures/box/map-box-foam-bump@lg.webp#texture',
                mapLightIn: '/textures/box/map-box-foam-shadow@lg.webp#texture',
                mapAOIn: '/textures/box/map-box-foam-ao@lg.webp#texture',
            },
            ...(hasCompressedTextures
                ? {
                      mapMatcapMatte: '/textures/box/map-box-matcap-rough-updated-etc1s.ktx2',
                      mapBumpOut: '/textures/box/map-box-noise-etc1s.ktx2',
                      mapAOOut: '/textures/box/map-box-out-ao-updated-etc1s.ktx2',
                      mapLightOut: '/textures/box/map-box-out-shadow-updated-etc1s.ktx2',

                      mapNoiseIn: '/textures/box/map-box-foam-bump-etc1s.ktx2',
                      mapLightIn: '/textures/box/map-box-foam-shadow-updated-etc1s.ktx2',
                      mapAOIn: '/textures/box/map-box-foam-ao-updated-etc1s.ktx2',
                  }
                : {}),
        },
        onLoadComplete
    );
    // const audio = useAudio();

    // const playBoxRevealSound = () => {
    //     audio.play('boxOpen');
    // };

    const setColor = (color) => (propsReactive.color = color);

    onMounted(() => {
        setRGBImage(props.image);
        window.object = object;
        window.shaderOut = shaderOut;
        window.shaderFoam = shaderFoam;
        window.baseMaterial = baseMaterial;
    });

    const setRGBImage = (url) => {
        if (!url) {
            logger.warnOnce('Box RGB texture URL missing', {
                event: 'asset.texture_missing',
                onceKey: 'asset.texture_missing:Box:rgb',
                textureKind: 'rgb',
                component: 'Box',
            });
            return;
        }
        loadBitmapTexture(url, { flipY: true }).then((texture) => {
            shaderOut.tRGB?.dispose();
            shaderOut.tRGB = texture;

            readyCount++;
            checkReady();
        });
    };

    const setVisible = () => {
        if (!boxInner.value) return;
        boxInner.value.visible = visible.value;
    };

    const show = () => {
        tlAnim?.kill();

        propsReactive.motionAmb = +props.hasAmbientMotion;
        // propsReactive.motionAmb = 0;
        propsReactive.lightMapIntensity = 1;

        tlAnim?.kill();
        tlAnim = gsap.timeline({
            onUpdate: updateTweens,
            onStart: () => (visible.value = true),
        });
        tlAnim.set(tweens, {
            introProgress: 0,
            openProgress: 0,
            downProgress: 0,
            backProgress: 0,
        });
        tlAnim.to(tweens, { introProgress: 1, duration: 2, ease: 'power3.out' }, 0);

        closeBoxDoors();
    };

    const open = () => {
        tlAnim?.kill();

        tlAnim = gsap.timeline({
            onStart: () => (visible.value = true),
            onUpdate: updateTweens,
            onComplete: () => (visible.value = false),
        });
        tlAnim.set(tweens, { introProgress: 1, openProgress: 0, downProgress: 0, backProgress: 0 });
        tlAnim.to(tweens, { openProgress: 1, duration: 2, ease: 'power3.out' }, 0);
        tlAnim.to(tweens, { backProgress: 1, duration: 1, ease: 'sine.inOut' }, 0);
        tlAnim.to(tweens, { downProgress: 1, duration: 2, ease: 'sine.inOut' }, 0.3);
        tlAnim.fromTo(
            propsReactive,
            { lightMapIntensity: 1 },
            { lightMapIntensity: 0, duration: 1.5, ease: 'sine.out', delay: 1.2 },
            0.1
        );
        tlAnim.fromTo(
            propsReactive,
            { motionAmb: 1 },
            { motionAmb: 0, duration: 1, ease: 'sine.out' },
            0
        );

        openBoxDoors();
    };

    const hide = ({ delay = 0 } = {}) => {
        if (delay) {
            hideDelay?.kill();
            hideDelay = gsap.delayedCall(delay, hideComplete);
        } else {
            hideComplete();
        }
    };

    const hideComplete = () => {
        visible.value = false;
        if (boxInner.value) boxInner.value.visible = false;

        gsap.set(tweens, {
            introProgress: 1,
            openProgress: 1,
            downProgress: 1,
            backProgress: 1,
        });

        updateTweens();
    };

    const reset = () => {
        tlAnim?.kill();
        visible.value = false;
        if (boxInner.value) boxInner.value.visible = false;

        gsap.set(tweens, { introProgress: 0, openProgress: 0, downProgress: 0, backProgress: 0 });
        updateTweens();
    };

    let tl;
    const setupIntroAnimation = () => {
        tl?.kill();
        tl = gsap.timeline({ paused: true }).addLabel('start');
    };

    watch(() => props.color, setColor);
    watch(() => props.image, setRGBImage);
    watch(visible, setVisible);
    watch(propsReactive, setUniforms, { immediate: true });

    defineExpose({
        show,
        hide,
        open,
        reset,
        object,
    });

    const openBoxDoors = () => {
        if (!animations.value || animations.value.length === 0) {
            return;
        }

        animations.value.forEach((animation) => {
            const action = animation.action;
            action.stop();
            action.reset();
            action.time = 0;
            action.setEffectiveWeight(1);
            action.play();
        });
    };

    const closeBoxDoors = () => {
        if (!animations.value || animations.value.length === 0) {
            return;
        }

        animations.value.forEach((animation) => {
            const action = animation.action;
            action.stop();
            action.setEffectiveWeight(0);
            action.reset();
            action.time = 0;
        });
    };

    const position = ref({ x: vPosIntro.x, y: vPosIntro.y, z: vPosIntro.z });
    const rotation = ref({ x: vRotIntro.x, y: vRotIntro.y, z: vRotIntro.z });

    const modelExport = useModelExporter();

    if (!modelExport) {
        const pane = usePane(
            [
                { value: propsReactive, options: { step: 0.01 } },
                { name: 'position', value: position, options: { step: 0.01 } },
                { name: 'rotation', value: rotation, options: { step: 0.01 } },
                {
                    name: 'tweens',
                    value: tweens,
                    options: {
                        ...basePaneOptions,
                        onChange: updateTweens,
                    },
                },
            ],
            {
                title: 'Box',
                expanded: false,
            }
        );

        if (pane) {
            pane.addButton({ title: 'Open' }).on('click', openBoxDoors);
            pane.addButton({ title: 'Close' }).on('click', closeBoxDoors);
        }
    }

    const emit = defineEmits(['loaded']);
</script>
